import React, { useState, useEffect, useContext } from 'react'
import { AppContext } from 'Context/AppContext';
import Card from 'Components/DesignSystem/Card/Card';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Alert from 'Components/DesignSystem/Alert/Alert';
import Typography from 'Components/DesignSystem/Typography/Typography';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Tooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import { getRecommenderModelsForGuide } from '../ModelServices/ModalServices';
import { TCategoryOption, TConnector, TField, TIndexState, TPredictiveData, TProductCategory, TRecommenderModel, TSubmodel, TUpsertISRProjectDataModelsInput } from '../model';
import HoverTooltip from 'Components/Common/components/HoverTooltip';

type TPredictiveComponentProps = {
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    setUserProjectDataModels: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    indexInfo: TIndexState;
};

const PredictiveComponent = ({ selectedItem, userProjectDataModels, setUserProjectDataModels, indexInfo }: TPredictiveComponentProps) => {
    const [isPredictive, setIsPredictive] = useState<boolean>(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
    const [selectedRecommenderId, setSelectedRecommenderId] = useState<string>("");
    const [availableCategories, setAvailableCategories] = useState<TCategoryOption[]>([]);
    const [recommenderModels, setRecommenderModels] = useState<TRecommenderModel[]>([]);
    const [isLoadingModels, setIsLoadingModels] = useState<boolean>(false);
    const appData = useContext(AppContext);
    const channelKey = appData?.channelKey;

    // Initialize state from existing data
    useEffect(() => {
        if (selectedItem?.type === "Category") {
            const categoryItem = selectedItem as TProductCategory;

            // Set predictive toggle state
            setIsPredictive(!!categoryItem.predictive?.isPredictive);

            // Set base category if it exists
            if (categoryItem.predictive?.baseCategory && categoryItem.predictive.baseCategory.length > 0) {
                const baseCat = categoryItem.predictive.baseCategory[0];
                const baseCatId = typeof baseCat === 'string' ? baseCat : baseCat.id;
                setSelectedCategoryId(baseCatId);

                // If there's a base category and recommender ID but no models loaded, fetch them
                if (categoryItem.predictive.recommenderId && !recommenderModels.length) {
                    fetchRecommenderModels(baseCatId);
                }
            } else {
                setSelectedCategoryId("");
            }

            // Set recommender model if it exists
            setSelectedRecommenderId(categoryItem.predictive?.recommenderId || "");
        }
    }, [selectedItem]);

    // Memoize available categories to prevent unnecessary recalculations
    useEffect(() => {
        if (!userProjectDataModels?.project) return;

        const collectCategories = () => {
            // Use a Map to track unique categories by ID
            const uniqueCategoriesMap = new Map<string, TCategoryOption>();
            const currentSelectedCategoryIds = getCurrentSelectedCategoryData().map(cat => cat.id);

            // Process all data models
            userProjectDataModels.project.dataModels?.forEach((dataModel, dataModelIndex) => {
                dataModel?.data?.productCategories?.forEach((productCategory, categoryIndex) => {
                    // Skip the current selected category
                    if (dataModelIndex === indexInfo.dataModelIndex && categoryIndex === indexInfo.categoryIndex) {
                        return;
                    }

                    productCategory?.data?.categories?.data?.forEach(category => {
                        // Skip categories that are in the current selected category
                        if (!currentSelectedCategoryIds.includes(category.id) && !uniqueCategoriesMap.has(category.id)) {
                            uniqueCategoriesMap.set(category.id, {
                                id: category.id,
                                name: category.name,
                                type: productCategory?.data?.categories?.type || "",
                                submodelName: dataModel?.data?.name,
                                categoryName: productCategory?.data?.name,
                                categoryId: productCategory?.id,
                                submodelId: dataModel?.id,
                            });
                        }
                    });
                });
            });

            // Process root level categories if any
            userProjectDataModels.project.productCategories?.forEach((productCategory, categoryIndex) => {
                // Skip the current selected category if it's at root level
                if (indexInfo.categoryOutsideDataModelIndex === categoryIndex) {
                    return;
                }

                productCategory?.data?.categories?.data?.forEach(category => {
                    // Skip categories that are in the current selected category
                    if (!currentSelectedCategoryIds.includes(category.id) && !uniqueCategoriesMap.has(category.id)) {
                        uniqueCategoriesMap.set(category.id, {
                            id: category.id,
                            name: category.name,
                            type: productCategory?.data?.categories?.type || "",
                            submodelName: "Root",
                            categoryName: productCategory?.data?.name,
                            categoryId: productCategory.id,
                        });
                    }
                });
            });

            // Convert Map values to array
            return Array.from(uniqueCategoriesMap.values());
        };

        setAvailableCategories(collectCategories());
    }, [userProjectDataModels, indexInfo, selectedItem]);

    // Fetch recommender models when base category changes or on initial load
    useEffect(() => {
        if (selectedCategoryId) {
            fetchRecommenderModels(selectedCategoryId);
        }
    }, [selectedCategoryId]);

    // Get the current selected category's data array
    const getCurrentSelectedCategoryData = () => {
        if (selectedItem?.type === "Category") {
            const categoryItem = selectedItem as TProductCategory;
            return categoryItem?.data?.categories?.data || [];
        }
        return [];
    };

    // Check if the selected category has categories data
    const hasCategories = (): boolean => {
        return getCurrentSelectedCategoryData().length > 0;
    };

    const categoriesExist = hasCategories();

    // Fetch recommender models
    const fetchRecommenderModels = async (categoryId: string) => {
        if (!categoryId || !channelKey) return;

        setIsLoadingModels(true);
        try {
            const response = await getRecommenderModelsForGuide(
                channelKey,
                [categoryId],
                getCurrentSelectedCategoryData().map(cat => cat.id)
            );

            if (response?.data && Array.isArray(response.data) && response.done) {
                setRecommenderModels(response.data);

                // If saved recommender ID does not exist in the new list, clear it
                if (selectedRecommenderId && !response.data.some((model: TRecommenderModel) => model.model_id === selectedRecommenderId)) {
                    setSelectedRecommenderId("");
                    updateProductCategory({ recommenderId: "" });
                }
            } else {
                setRecommenderModels([]);
            }
        } catch (error) {
            console.error("Error fetching recommender models:", error);
            setRecommenderModels([]);
        } finally {
            setIsLoadingModels(false);
        }
    };

    const handleCategoryChange = (categoryId: string) => {
        setSelectedCategoryId(categoryId);
        setSelectedRecommenderId("");
        const selectedCategory = availableCategories.find(cat => cat.id === categoryId);
        updateProductCategory({
            baseCategory: categoryId && selectedCategory ? [{
                id: selectedCategory.id,
                categoryId: selectedCategory.categoryId,
                submodelId: selectedCategory.submodelId
            }] : undefined,
            recommenderId: undefined
        });
    };

    const handleRecommenderChange = (recommenderId: string) => {
        setSelectedRecommenderId(recommenderId);
        updateProductCategory({ recommenderId: recommenderId || undefined });
    };

    const handlePredictiveToggle = () => {
        const newState = !isPredictive;
        setIsPredictive(newState);

        // If turning off predictive, clear related fields
        if (!newState) {
            setSelectedCategoryId("");
            setSelectedRecommenderId("");
            updateProductCategory({
                isPredictive: false,
                baseCategory: undefined,
                recommenderId: undefined
            });
        } else {
            updateProductCategory({ isPredictive: true });
        }
    };

    // Update the product category with predictive settings
    const updateProductCategory = (updates: Partial<TPredictiveData>) => {
        if (!selectedItem || selectedItem.type !== "Category") return;

        setUserProjectDataModels(prevState => {
            // Create a deep copy to avoid mutation
            const newState = JSON.parse(JSON.stringify(prevState));
            let targetCategory: TProductCategory | undefined;

            // Find and update in data models
            if (typeof indexInfo.dataModelIndex === 'number' && typeof indexInfo.categoryIndex === 'number') {
                const dataModelIndex = indexInfo.dataModelIndex;
                const categoryIndex = indexInfo.categoryIndex;

                if (newState.project.dataModels[dataModelIndex]?.data?.productCategories?.[categoryIndex]) {
                    targetCategory = newState.project.dataModels[dataModelIndex].data.productCategories[categoryIndex];
                }
            }
            // Find and update at root level if needed
            else if (typeof indexInfo.categoryOutsideDataModelIndex === 'number') {
                const categoryIndex = indexInfo.categoryOutsideDataModelIndex;

                if (newState.project.productCategories?.[categoryIndex]) {
                    targetCategory = newState.project.productCategories[categoryIndex];
                }
            }

            // Apply updates if target category was found
            if (targetCategory) {
                if (!targetCategory.predictive) {
                    targetCategory.predictive = { isPredictive: false };
                }

                // Apply updates with proper type checking
                if ('isPredictive' in updates && updates.isPredictive !== undefined) {
                    targetCategory.predictive.isPredictive = updates.isPredictive;
                }
                if ('baseCategory' in updates) {
                    targetCategory.predictive.baseCategory = updates.baseCategory;
                }
                if ('recommenderId' in updates) {
                    targetCategory.predictive.recommenderId = updates.recommenderId;
                }
            }

            return newState;
        });
    };

    /*
        ONE Card that grows, not two elements faking one.
        It used to be a `cart-style-dm` header with `rounded-b-none border-b-0` plus a
        separate hand-rolled div below repeating the border — a single card drawn as
        two, which had to be kept in sync by hand.
    */
    return (
        <Card id="predictive-component" data-testid="predictive-component" className="tw-mb-4">
            <div className="tw-flex tw-justify-between tw-items-center">
                <div className="tw-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" as="h3" className="tw-text-ink">
                        Predictive
                    </Typography>
                    <InfoTooltip
                        data-testid="predictive-component-info"
                        placement="top"
                        message="Determines which products appear in this category at runtime using a Commend model."
                    />
                </div>
                {/*
                    A DS Toggle, not a hand-rolled `role="switch"` button.
                    The old one reimplemented the switch with `bg-green-600`/`bg-gray-300`
                    — neither of which is a design-system track tier — and, more to the
                    point, had NO disabled styling whatsoever: it rendered identically
                    whether or not `categoriesExist`, so the reason it would not respond
                    was invisible. DS Toggle carries the disabled tier (§ 123).
                */}
                <Tooltip
                    content="Please choose a category to enable predictive"
                    disabled={categoriesExist}
                    placement="top"
                >
                    <span>
                        <Toggle
                            id="predictive-component-toggle"
                            data-testid="components-predictivecomponent-button-1"
                            label=""
                            aria-label="Toggle predictive"
                            checked={isPredictive}
                            onChange={handlePredictiveToggle}
                            disabled={!categoriesExist}
                        />
                    </span>
                </Tooltip>
            </div>

            {isPredictive && (
                <div id="predictive-component-body" className="tw-mt-3">
                    <Alert tone="warning" data-testid="predictive-component-runtime-notice">
                        Note: Runtime support is not yet shipped. This setting has no effect
                        until it does.
                    </Alert>
                    <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mt-3">
                        <div>
                            <FieldTitle label="Base Category" required data-testid="predictive-component-base-category-label" />
                            <Dropdown
                                id="predictive-component-base-category"
                                data-testid="components-predictivecomponent-select-2"
                                label=""
                                aria-label="Base Category"
                                placeholder="Select"
                                options={[
                                    { value: '', label: 'Select' },
                                    ...availableCategories.map((category) => ({
                                        value: category.id,
                                        label: `${category.submodelName}.${category.categoryName}`,
                                    })),
                                ]}
                                value={selectedCategoryId}
                                onChange={(value) => handleCategoryChange(String(value))}
                                usePortal
                                className="tw-w-60"
                            />
                        </div>
                        <div>
                            <FieldTitle label="Recommender model" data-testid="predictive-component-recommender-label" />
                            <Dropdown
                                id="predictive-component-recommender"
                                data-testid="components-predictivecomponent-select-3"
                                label=""
                                aria-label="Recommender model"
                                placeholder={isLoadingModels ? 'Loading...' : 'Select'}
                                options={[
                                    { value: '', label: isLoadingModels ? 'Loading...' : 'Select' },
                                    ...recommenderModels.map((model) => ({
                                        value: model.model_id,
                                        label: model.model_name,
                                    })),
                                ]}
                                value={selectedRecommenderId}
                                onChange={(value) => handleRecommenderChange(String(value))}
                                disabled={isLoadingModels || !selectedCategoryId}
                                usePortal
                                className="tw-w-60"
                            />
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default PredictiveComponent;